/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from "./api";
import { Preferences } from "@capacitor/preferences";

// ==================== INTERFACES ====================

 
export interface Profile {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export type RelationshipStatus =
  | "pending"
  | "accepted"
  | "rejected"
  | "blocked";
export type RelationshipType = "follow" | "friend" | "block" | "share";

export interface FollowerRelation {
  id: number;
  status: RelationshipStatus;
  relationship_type: RelationshipType;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
  rejected_at: string | null;
  blocked_at: string | null;
  follower_profile_id: Profile | number;
  following_profile_id: Profile | number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ApiResponse<T> {
  message: string;
  data: T;
  pagination?: Pagination;
}

export interface FollowStats {
  followers_count: number;
  following_count: number;
  pending_requests_count: number;
}
export interface RelationStats {
  blocked_count: number;
  followers_count: number;
  following_count: number;
  pending_incoming: {
    follow_requests: number;
    follower_requests: number;
    total: number;
  };
  pending_outgoing: {
    follow_requests: number;
    follower_requests: number;
    total: number;
  };
}
export interface RelationshipStatusData {
  user1_follows_user2: FollowerRelation | null;
  user2_follows_user1: FollowerRelation | null;
  are_friends: boolean;
}

// Request interfaces
export interface FollowUserRequest {
  following_profile_id: string | number;
  relationship_type?: RelationshipType;
}

export interface UnfollowUserRequest {
  follower_profile_id?: string | number;
  following_profile_id?: string | number;
  relationship_type?: RelationshipType;
}

export interface AcceptFollowRequest {
  follower_profile_id: string | number;
}

export interface RejectFollowRequest {
  follower_profile_id: string | number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface FollowersQueryParams extends PaginationParams {
  status?: RelationshipStatus;
}

// Existing share interfaces
export interface ShareContact {
  type: "phone" | "email";
  value: string;
}

export interface ShareDataPayload {
  profileId: string;
  contact: ShareContact;
}

// ==================== UTILITY FUNCTIONS ====================

// Format follower data for consistent display
const formatFollowerData = (
  relations: FollowerRelation[]
): FollowerRelation[] => {
  return relations.map((relation) => {
    const createdDate = new Date(relation.created_at);
    const dateStr = createdDate.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return {
      ...relation,
      created_at: dateStr,
      // Ensure profile data is properly formatted
      follower_profile_id:
        typeof relation.follower_profile_id === "object"
          ? {
              ...relation.follower_profile_id,
              display_name:
                `${relation.follower_profile_id.first_name} ${relation.follower_profile_id.last_name}`.trim(),
            }
          : relation.follower_profile_id,
      following_profile_id:
        typeof relation.following_profile_id === "object"
          ? {
              ...relation.following_profile_id,
              display_name:
                `${relation.following_profile_id.first_name} ${relation.following_profile_id.last_name}`.trim(),
            }
          : relation.following_profile_id,
    };
  });
};

// Store relationship data in Capacitor Preferences
const storeRelationshipData = async (key: string, data: any): Promise<void> => {
  try {
    await Preferences.set({
      key,
      value: JSON.stringify(data),
    });
  } catch (error) {
    console.error(`Failed to store ${key} in preferences:`, error);
  }
};

// ==================== FOLLOW MANAGEMENT ====================

// Send a follow request to another user
export const followUser = async (
  payload: FollowUserRequest
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/follow",
      payload
    );

    // Store the new relationship in preferences
    await storeRelationshipData("lastFollowAction", response.data.data);

    return response.data.data;
  } catch (error: any) {
    // Standardized error handling
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 400) {
        if (errorMessage.includes("Cannot follow yourself")) {
          throw new Error("Cannot follow yourself");
        } else if (errorMessage.includes("Relationship already exists")) {
          throw new Error("Already following this user");
        } else {
          throw new Error(`Bad request: ${errorMessage}`);
        }
      } else if (status === 404) {
        throw new Error("User not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

// Unfollow a user (remove the relationship)
export const unfollowUser = async (
  payload: UnfollowUserRequest
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/unfollow",
      payload
    );

    // Store the action in preferences
    await storeRelationshipData("lastUnfollowAction", response.data.data);

    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 404) {
        throw new Error("Relationship not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

// ==================== FOLLOW REQUEST MANAGEMENT ====================

// Accept a pending follow request
export const acceptFollowRequest = async (
  payload: any
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/accept-follow-request",
      payload
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 404) {
        throw new Error("Pending follow request not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

// Reject a pending follow request
export const rejectFollowRequest = async (
  payload: any
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/reject-follow-request",
      payload
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 404) {
        throw new Error("Pending follow request not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

export const acceptInvitationAPI = async (payload: any): Promise<any> => {
  try {
    const response = await apiClient.post<ApiResponse<any>>(
      "/api/accept-invitation",
      payload
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";

      if (status === 404) {
        throw new Error("Invitation not found");
      } else {
        throw new Error(`${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
}
// Get all pending follow requests for the current user
export const getPendingRequests = async (
  params?: PaginationParams
): Promise<FollowerRelation[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/v2/invitations/sent",
      { params }
    );

    const formattedRequests =
      response.data.data.length > 0
        ? formatFollowerData(response.data.data)
        : [];

    // Store pending requests in preferences
    await storeRelationshipData("pendingRequests", {
      data: formattedRequests,
      pagination: response.data.pagination,
      lastUpdated: new Date().toISOString(),
    });

    return formattedRequests;
  } catch (error) {
    console.error("Error fetching pending requests:", error);
    throw error;
  }
};

// ==================== RELATIONSHIP QUERIES ====================

// Get all followers for a user profile
export const getAllFollowers = async (
  profileId?: string | number,
  params?: FollowersQueryParams
): Promise<FollowerRelation[]> => {
  try {
    const url = profileId ? `/api/followers/${profileId}` : "/api/followers";
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(url, {
      params,
    });

    const followersData = response.data.data;
    const formattedFollowers =
      followersData.length > 0 ? formatFollowerData(followersData) : [];

    // Store followers in Capacitor Preferences
    await storeRelationshipData("userFollowers", {
      profileId,
      data: formattedFollowers,
      pagination: response.data.pagination,
      lastUpdated: new Date().toISOString(),
    });

    return formattedFollowers;
  } catch (error) {
    console.error("Error fetching followers:", error);
    throw error;
  }
};

// Get all users that a profile is following
export const getAllFollowing = async (
  profileId?: string | number,
  params?: FollowersQueryParams
): Promise<any[]> => {
  try {
    const url = profileId ? `/api/following/${profileId}` : "/api/following";
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(url, {
      params,
    });

    const followingData = response.data.data;
    const formattedFollowing =
      followingData.length > 0 ? formatFollowerData(followingData) : [];

    // Store following data in preferences
    await storeRelationshipData("userFollowing", {
      profileId,
      data: formattedFollowing,
      pagination: response.data.pagination,
      lastUpdated: new Date().toISOString(),
    });

    return formattedFollowing;
  } catch (error) {
    console.error("Error fetching following:", error);
    throw error;
  }
};

// Get follower and following statistics for a profile
export const getFollowStats = async (
  profileId?: string | number
): Promise<FollowStats> => {
  try {
    const url = profileId
      ? `/api/follow/stats/${profileId}`
      : "/api/follow/stats";
    const response = await apiClient.get<ApiResponse<FollowStats>>(url);

    // Store stats in preferences
    await storeRelationshipData("followStats", {
      profileId,
      data: response.data.data,
      lastUpdated: new Date().toISOString(),
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching follow stats:", error);
    throw error;
  }
};
export const relationStats = async (
  profileId?: string | number
): Promise<RelationStats> => {
  try {
  const url  = '/api/follower-stats';
  const response = await apiClient.get<ApiResponse<RelationStats>>(url);

  // Store stats in preferences
  await storeRelationshipData("relationStats", {
    profileId,
    data: response.data.data,
    lastUpdated: new Date().toISOString(),
  });

  return response.data.data;
} catch (error) {
  console.error("Error fetching relation stats:", error);
  throw error;
}
}

// Manual implementation of relationship status check
export const getRelationshipStatus = async (
  profileId1: string | number,
  profileId2: string | number
): Promise<RelationshipStatusData> => {
  try {
    // Get following list for profileId1 to see if they follow profileId2
    const following1 = await getAllFollowing(profileId1);
    const user1FollowsUser2 = following1.find((relation) => {
      const followingId =
        typeof relation.following_profile_id === "object"
          ? relation.following_profile_id.id
          : relation.following_profile_id;
      return followingId === Number(profileId2);
    });

    // Get following list for profileId2 to see if they follow profileId1
    const following2 = await getAllFollowing(profileId2);
    const user2FollowsUser1 = following2.find((relation) => {
      const followingId =
        typeof relation.following_profile_id === "object"
          ? relation.following_profile_id.id
          : relation.following_profile_id;
      return followingId === Number(profileId1);
    });

    const relationshipStatus = {
      user1_follows_user2: user1FollowsUser2 || null,
      user2_follows_user1: user2FollowsUser1 || null,
      are_friends: !!(user1FollowsUser2 && user2FollowsUser1),
    };

    // Store relationship status in preferences
    await storeRelationshipData(
      `relationshipStatus_${profileId1}_${profileId2}`,
      {
        data: relationshipStatus,
        lastUpdated: new Date().toISOString(),
      }
    );

    return relationshipStatus;
  } catch (error) {
    console.error("Error getting relationship status:", error);
    return {
      user1_follows_user2: null,
      user2_follows_user1: null,
      are_friends: false,
    };
  }
};

// ==================== UTILITY FUNCTIONS ====================

// Check if current user is following a specific user
export const isFollowing = async (
  targetProfileId: string | number
): Promise<boolean> => {
  try {
    const following = await getAllFollowing();
    return following.some(
      (relation) =>
        relation.following_profile_id === targetProfileId ||
        (typeof relation.following_profile_id === "object" &&
          relation.following_profile_id.id === Number(targetProfileId))
    );
  } catch (error) {
    console.error("Error checking follow status:", error);
    return false;
  }
};

// Check if current user is followed by a specific user
export const isFollowedBy = async (
  followerProfileId: string | number
): Promise<boolean> => {
  try {
    const followers = await getAllFollowers();
    return followers.some(
      (relation) =>
        relation.follower_profile_id === followerProfileId ||
        (typeof relation.follower_profile_id === "object" &&
          relation.follower_profile_id.id === Number(followerProfileId))
    );
  } catch (error) {
    console.error("Error checking follower status:", error);
    return false;
  }
};

// Check if two users are mutual friends (follow each other)
export const areMutualFriends = async (
  profileId1: string | number,
  profileId2: string | number
): Promise<boolean> => {
  try {
    const relationshipStatus = await getRelationshipStatus(
      profileId1,
      profileId2
    );
    return relationshipStatus.are_friends;
  } catch (error) {
    console.error("Error checking mutual friendship:", error);
    return false;
  }
};

// Get follower count only (lightweight stats call)
export const getFollowerCount = async (
  profileId?: string | number
): Promise<number> => {
  try {
    const stats = await getFollowStats(profileId);
    return stats.followers_count;
  } catch (error) {
    console.error("Error getting follower count:", error);
    return 0;
  }
};

// Get following count only (lightweight stats call)
export const getFollowingCount = async (
  profileId?: string | number
): Promise<number> => {
  try {
    const stats = await getFollowStats(profileId);
    return stats.following_count;
  } catch (error) {
    console.error("Error getting following count:", error);
    return 0;
  }
};

// Bulk follow multiple users
export const followMultipleUsers = async (
  userIds: (string | number)[]
): Promise<FollowerRelation[]> => {
  const results: FollowerRelation[] = [];

  for (const userId of userIds) {
    try {
      const result = await followUser({ following_profile_id: userId });
      results.push(result);
    } catch (error) {
      console.error(`Failed to follow user ${userId}:`, error);
      // Continue with other users even if one fails
    }
  }

  return results;
};

// Bulk unfollow multiple users
export const unfollowMultipleUsers = async (
  userIds: (string | number)[]
): Promise<FollowerRelation[]> => {
  const results: FollowerRelation[] = [];

  for (const userId of userIds) {
    try {
      const result = await unfollowUser({ following_profile_id: userId });
      results.push(result);
    } catch (error) {
      console.error(`Failed to unfollow user ${userId}:`, error);
      // Continue with other users even if one fails
    }
  }

  return results;
};

// ==================== EXISTING FUNCTION ====================

export function shareDataWithContact(payload: ShareDataPayload) {
  console.log("Sharing data:", payload);
}
/**
 * Get followers for the current user (authenticated profile).
 */
export const getMyFollowers = async (
  params?: FollowersQueryParams
): Promise<any[]> => {
  return getAllFollowers(undefined, params);
};

/**
 * Get users the current user is following.
 */
export const getMyFollowing = async (
  params?: FollowersQueryParams
): Promise<any[]> => {
  return getAllFollowing(undefined, params);
};

/**
 * Get pending follow requests sent to the current user.
 */
export const getFollowerRequests = async (params?: any): Promise<any[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/follower-requests",
      { params }
    );
    return response.data.data.length > 0
      ? formatFollowerData(response.data.data)
      : [];
  } catch (error) {
    console.error("Error fetching pending requests for me:", error);
    throw error;
  }
};
/**
 * Get pending follow requests sent to the current user.
 */
export const getPendingRequestsForMe = async (params?: any): Promise<any[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/pending-requests-for-me",
      { params }
    );
    return response.data.data.length > 0
      ? formatFollowerData(response.data.data)
      : [];
  } catch (error) {
    console.error("Error fetching pending requests for me:", error);
    throw error;
  }
};

/**
 * Get pending follow requests sent by the current user.
 */
export const getFollowingRequests = async (params?: any): Promise<any[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/following-requests",
      { params }
    );
    return response.data.data.length > 0
      ? formatFollowerData(response.data.data)
      : [];
  } catch (error) {
    console.error("Error fetching my pending requests:", error);
    throw error;
  }
};
/**
 * Get pending follow requests sent by the current user.
 */
export const getMyPendingRequests = async (params?: any): Promise<any[]> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/pending-requests",
      { params }
    );
    return response.data.data.length > 0
      ? formatFollowerData(response.data.data)
      : [];
  } catch (error) {
    console.error("Error fetching my pending requests:", error);
    throw error;
  }
};

/**
 * Cancel a pending follow request sent by the current user.
 */
export const cancelFollowRequest = async (
  payload: any
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/cancel-follow-request",
      payload
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";
      if (status === 404) {
        throw new Error("Pending follow request not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};

/**
 * Remove a follower from the current user's followers.
 */
export const removeFollower = async (
  payload: UnfollowUserRequest
): Promise<FollowerRelation> => {
  try {
    const response = await apiClient.post<ApiResponse<FollowerRelation>>(
      "/api/remove-follower",
      payload
    );
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage =
        error.response.data?.message || "Unknown server error";
      if (status === 404) {
        throw new Error("Follower not found");
      } else {
        throw new Error(`Server error: ${errorMessage}`);
      }
    } else if (error.request) {
      throw new Error("No response from server. Please check your connection.");
    } else {
      throw error;
    }
  }
};
export const pendingRequestsActionRequired = async (): Promise<any> => {
  try {
    const response = await apiClient.get<ApiResponse<FollowerRelation[]>>(
      "/api/follow/actions-required"
    );
    return response.data.data;
  } catch (error) {
    console.error("Error checking pending requests action required:", error);
    return false;
  }
};
